import { compareAsc, parse } from "date-fns";
import { ColumnKeyEnum } from "../../../constants";
import { IVariantMapInputs } from "../models/VariantMap";

export enum GeneTypeEnum {
  xiagibbs = "AHDC1",
  werner = "WRN",
  rothmund = "RECQL4",
  bloom = "BLM",
}

export const getGeneMapOptions = (
  gridApi: any,
  gene: GeneTypeEnum,
  dateNewCase?: Date
): IVariantMapInputs | null => {
  if (!gridApi) {
    return null;
  }

  const geneData = getGeneChartData(gridApi, dateNewCase);
  const geneTypeData = getGeneType(gene);

  let geneMapInputs: IVariantMapInputs = {
    ...geneTypeData,
    variant_sets: {},
  };

  if (geneData.newCases.positions.length) {
    geneMapInputs = {
      ...geneMapInputs,
      variant_sets: {
        "1": {
          label: dateNewCase ? "New Cases" : "Cases",
          orientation: "top",
          positions: geneData.newCases.positions,
          names: geneData.newCases.names,
          color: "#FFD92F",
        },
      },
    };
  }

  if (geneData.prevCases.positions.length) {
    geneMapInputs = {
      ...geneMapInputs,
      variant_sets: {
        ...geneMapInputs.variant_sets,
        "2": {
          label: "Previous Cases",
          orientation: "bottom",
          positions: geneData.prevCases.positions,
          names: geneData.prevCases.names,
          color: "#E5C494",
        },
      },
    };
  }

  return geneMapInputs;
};

export const getGeneChartData = (gridApi: any, dateNewCase?: Date) => {
  const newCasesNames: string[] = [];
  const newCasesPositions: number[] = [];
  const prevCasesNames: string[] = [];
  const prevCasesPositions: number[] = [];

  const newUniqueTracker = new Set();
  const prevUniqueTracker = new Set();

  // iterate only nodes that pass the filter
  gridApi.forEachNodeAfterFilter((rowNode, index) => {
    const rowGeneId = rowNode.data[ColumnKeyEnum.aa_id];
    const rowGenePosition = rowNode.data[ColumnKeyEnum.aa_position];

    let isNewCase = true;

    if (dateNewCase) {
      const rowCreationDate = rowNode.data[ColumnKeyEnum.created_date];

      if (rowCreationDate) {
        // Is it before or after date new case line
        // TODO: Need to be part of the column?
        const dateRowCase = parse(
          rowCreationDate,
          "yyyy-MM-dd HH:mm:ss",
          new Date()
        );

        // Jan 20 2022 compared to Jun 16 2022 returns -1
        // Is the row case more recent than the new case line
        const isCompareOlder = compareAsc(dateRowCase, dateNewCase);
        isNewCase = isCompareOlder === 1 ? true : false;
      }
    }

    if (isNewCase) {
      if (!newUniqueTracker.has(rowGeneId)) {
        if (rowGeneId) {
          newCasesNames.push(rowGeneId);
          newUniqueTracker.add(rowGeneId);
        }
        if (rowGenePosition) {
          newCasesPositions.push(rowGenePosition);
        }
      }
    } else {
      if (!prevUniqueTracker.has(rowGeneId)) {
        if (rowGeneId) {
          prevUniqueTracker.add(rowGeneId);

          prevCasesNames.push(rowGeneId);
        }

        if (rowGenePosition) {
          prevCasesPositions.push(rowGenePosition);
        }
      }
    }
  });

  return {
    newCases: {
      names: newCasesNames,
      positions: newCasesPositions,
    },
    prevCases: {
      names: prevCasesNames,
      positions: prevCasesPositions,
    },
  };
};

export const getGeneType = (geneType: GeneTypeEnum) => {
  let plot_bg = "";
  let chromosome = "";
  let xaxis: number[] = [];
  let domains_begin: number[] = [];
  let domains_end: number[] = [];
  let domains_names: string[] = [];
  let domains_colors: string[] = [];

  if (geneType === GeneTypeEnum.xiagibbs) {
    plot_bg = "transparent";
    chromosome = "chr1";
    xaxis = [0, 600, 1200, 1603];
    domains_begin = [394, 542, 721, 586, 835, 1193, 1116, 1601];
    domains_end = [408, 556, 735, 645, 860, 1405, 1161, 1603];
    domains_names = [
      "AT Hook",
      "AT Hook",
      "AT Hook",
      "Conserved with REV3L",
      "Conserved with KIAA2022",
      "Conserved with REV3L",
      "Conserved with REV3L and KIAA2022",
      "PDZ binding domain",
    ];
    domains_colors = [
      "#fb8072",
      "#fb8072",
      "#fb8072",
      "#8dd3c7",
      "#fdb462",
      "#8dd3c7",
      "#80b1d3",
      "#80b1d3",
    ];
  } else if (geneType === GeneTypeEnum.werner) {
    plot_bg = "transparent";
    chromosome = "chr8";
    xaxis = [0, 500, 1000, 1432];
    domains_begin = [60, 558, 749, 1150];
    domains_end = [228, 724, 899, 1229];
    domains_names = [
      "3'-5' exonuclease",
      "Helicase ATP-binding",
      "Helicase C-terminal",
      "HRDC",
    ];
    domains_colors = ["#fb8072", "#8dd3c7", "#fdb462", "#80b1d3"];
  } else if (geneType === GeneTypeEnum.rothmund) {
    plot_bg = "transparent";
    chromosome = "chr8";
    xaxis = [0, 400, 800, 1208];
    domains_begin = [489, 683];
    domains_end = [662, 850];
    domains_names = ["Helicase ATP-binding", "Helicase C-terminal"];
    domains_colors = ["#fb8072", "#8dd3c7"];
  } else if (geneType === GeneTypeEnum.bloom) {
    plot_bg = "transparent";
    chromosome = "chr15";
    xaxis = [0, 500, 1000, 1417];
    domains_begin = [676, 877, 1212];
    domains_end = [851, 1024, 1292];
    domains_names = ["Helicase ATP-binding", "Helicase C-terminal", "HRDC"];
    domains_colors = ["#fb8072", "#8dd3c7", "#fdb462"];
  }

  return {
    plot_bg,
    chromosome,
    xaxis,
    domains_begin,
    domains_end,
    domains_names,
    domains_colors,
  };
};
